/**
 * MainLayout Component
 *
 * Main application layout with header, collapsible side panel, footer,
 * and environment banner. Provides consistent layout structure for all
 * authenticated pages.
 *
 * Features:
 * - Header with hamburger menu (mobile) and user menu
 * - Collapsible SidePanel navigation
 * - Environment warning banner for test/dev environments
 * - Footer with version and links
 * - Responsive design
 *
 * Requirements: 2.1, 2.6, 2.7, 16.7
 */

import React from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Box,
    Container,
    Avatar,
    Badge,
    Button,
    CircularProgress,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { NavBarLogo } from './NavBarLogo';
import { VoiceRecipeLogo } from './VoiceRecipeLogo';
import { ReusableFooter } from './ReusableFooter';
import { UserMenu } from './UserMenu';
import { useTestEnvWarning } from '../hooks/useTestEnvWarning';
import { useInbox } from '../hooks/useInbox';
import { useUserProfile } from '../hooks/useUserProfile';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { SettingsDialog } from './SettingsDialog';
import { WelcomeDialog } from './WelcomeDialog';
import { UserProfileService } from '../services/user-profile-service';
import {
    SidePanel,
    SIDEBAR_EXPANDED_WIDTH,
    SIDEBAR_COLLAPSED_WIDTH,
    readCollapsedState,
    writeCollapsedState,
} from './SidePanel';

/**
 * Props for MainLayout component
 */
export interface MainLayoutProps {
    /** Child components to render in the main content area. When omitted, renders an Outlet for nested routes. */
    children?: React.ReactNode;
}

/**
 * MainLayout Component
 *
 * Provides the main application layout structure with a collapsible sidebar,
 * header, content area, and footer.
 */
export function MainLayout({ children }: MainLayoutProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    // While auth is loading, assume authenticated to avoid flashing the visitor UI
    const showAuthenticatedUI = isAuthenticated || authLoading;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    // Inbox unread count for avatar badge
    const { unreadCount } = useInbox();

    // User profile for AppBar avatar
    const { profile, isLoading: profileLoading } = useUserProfile();

    // Settings dialog state
    const [settingsOpen, setSettingsOpen] = React.useState(false);

    // Show notification if user just completed a social login AND account was linked
    // (only for existing users who already have a real username — new users get the profile setup flow)
    const { notify } = useNotification();
    React.useEffect(() => {
        if (profileLoading || !profile) return; // Wait for profile to load
        if (!isAuthenticated) return;

        const socialProvider = localStorage.getItem('social_login_provider');
        const emailLoginCompleted = localStorage.getItem('email_login_completed');

        // Case 1: Social login by a user who originally signed up with email/password
        if (socialProvider) {
            const username = profile.username || '';
            const isAutoGenerated = !username || username.startsWith('user_');

            if (isAutoGenerated) {
                // New user — clear flag silently, profile setup handles onboarding
                localStorage.removeItem('social_login_provider');
            } else if (profile.is_account_linked) {
                // Account already linked previously — no need to show notification again
                localStorage.removeItem('social_login_provider');
            } else if (profile.sign_up_method && profile.sign_up_method !== socialProvider.toLowerCase()) {
                // Existing user signed up with a different method — this is a link event
                localStorage.removeItem('social_login_provider');
                notify({
                    message: `Your ${socialProvider} account has been linked to your VoiceRecipe account. You can now sign in with either method. If you haven't set a VoiceRecipe password yet, use "Forgot Password" on the login page to create one.`,
                    severity: 'info',
                });
                // Persist the linked state so we don't show this again
                const service = new UserProfileService();
                service.updateProfile({ is_account_linked: true }).catch(() => {});
            } else {
                // Same sign-up method as current login (e.g. social-only user logging in with social) — no link
                localStorage.removeItem('social_login_provider');
            }
            return;
        }

        // Case 2: Email/password login by a user who originally signed up with social (Google)
        if (emailLoginCompleted) {
            localStorage.removeItem('email_login_completed');
            if (profile.is_account_linked) return; // Already linked
            if (profile.sign_up_method && profile.sign_up_method !== 'email') {
                // User originally signed up with social, now logging in with email — link event
                notify({
                    message: `Your email/password account has been linked to your VoiceRecipe account. You can now sign in with either method.`,
                    severity: 'info',
                });
                const service = new UserProfileService();
                service.updateProfile({ is_account_linked: true }).catch(() => {});
            }
        }
    }, [isAuthenticated, notify, profile, profileLoading]);

    // Welcome dialog for new users
    const [welcomeOpen, setWelcomeOpen] = React.useState(false);
    const welcomeShownRef = React.useRef(false);

    // Profile setup redirect for social login users who haven't completed profile setup
    const needsProfileSetup = React.useMemo(() => {
        if (profileLoading || !profile) return false;
        // Auto-generated usernames (user_XXXXXX) mean user hasn't completed profile setup
        const username = profile.username || '';
        const isAutoGenerated = !username || username.startsWith('user_');
        return isAutoGenerated && profile.welcome_dismissed !== true;
    }, [profile, profileLoading]);

    React.useEffect(() => {
        if (needsProfileSetup) {
            navigate('/login?setup=true', { replace: true });
        }
    }, [needsProfileSetup, navigate]);

    React.useEffect(() => {
        if (!profileLoading && profile && profile.welcome_dismissed !== true && !needsProfileSetup && !welcomeShownRef.current) {
            welcomeShownRef.current = true;
            setWelcomeOpen(true);
        }
    }, [profile, profileLoading, needsProfileSetup]);

    const handleWelcomeClose = async () => {
        setWelcomeOpen(false);
        try {
            const service = new UserProfileService();
            await service.updateProfile({ welcome_dismissed: true });
        } catch {
            // Silently ignore
        }
    };

    // Sidebar collapsed state — persisted to localStorage
    const [collapsed, setCollapsed] = React.useState<boolean>(() => readCollapsedState());
    // Mobile overlay drawer state
    const [mobileOpen, setMobileOpen] = React.useState(false);

    const isWide = useMediaQuery('(min-width:768px)');

    // Trigger test environment warning notification once per session
    useTestEnvWarning();

    // Show branded loading spinner only when redirecting to profile setup (new user)
    if (needsProfileSetup) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 3 }}>
                <VoiceRecipeLogo size="large" />
                <CircularProgress size={40} />
                <Typography variant="h6" color="text.secondary">
                    Setting up your VoiceRecipe account...
                </Typography>
            </Box>
        );
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleToggleCollapse = () => {
        setCollapsed((prev) => {
            const next = !prev;
            writeCollapsedState(next);
            return next;
        });
    };

    const handleMobileClose = () => {
        setMobileOpen(false);
    };

    const handleMobileOpen = () => {
        setMobileOpen(true);
    };

    const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Side Panel — only visible for authenticated users */}
            {showAuthenticatedUI && (
                <SidePanel
                    collapsed={collapsed}
                    onToggleCollapse={handleToggleCollapse}
                    mobileOpen={mobileOpen}
                    onMobileClose={handleMobileClose}
                />
            )}

            {/* Right side: AppBar + content + footer */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: '100vh',
                    width: showAuthenticatedUI && isWide ? `calc(100% - ${sidebarWidth}px)` : '100%',
                }}
            >
                {/* Header */}
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: 'transparent',
                        color: 'text.primary',
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' }, maxWidth: 1200, width: '100%', mx: 'auto' }}>
                        {/* Hamburger menu — visible only for authenticated users below 768px */}
                        {showAuthenticatedUI && !isWide && (
                            <IconButton
                                aria-label="open navigation"
                                edge="start"
                                onClick={handleMobileOpen}
                                sx={{ mr: 1 }}
                                data-testid="hamburger-menu"
                            >
                                <MenuIcon />
                            </IconButton>
                        )}

                        {/* Logo — always visible */}
                        <NavBarLogo size="small" />

                        {/* Navigation links */}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, flexGrow: 1, justifyContent: 'center' }}>
                            {[
                                { label: 'Home', path: '/' },
                                { label: 'Pricing', path: '/pricing' },
                                { label: 'Market', path: '/market' },
                                { label: 'Forum', path: '/forum' },
                                { label: 'Support', path: '/support' },
                            ].map((item) => (
                                <Button
                                    key={item.label}
                                    onClick={() => navigate(item.path)}
                                    sx={{
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        fontSize: '0.9rem',
                                        color: 'text.primary',
                                        borderRadius: '20px',
                                        px: 2,
                                        py: 0.5,
                                        backgroundColor: 'transparent',
                                        '&:hover': {
                                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.12)'
                                                : 'rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </Box>

                        {/* User Menu (authenticated) or Sign In button (visitor) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {showAuthenticatedUI ? (
                                <>
                                    <Badge
                                        badgeContent={unreadCount}
                                        color="error"
                                        max={99}
                                        invisible={unreadCount === 0}
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                top: 10,
                                                right: 10,
                                            },
                                        }}
                                    >
                                        <IconButton
                                            size="large"
                                            aria-label="account menu"
                                            aria-controls="user-menu"
                                            aria-haspopup="true"
                                            onClick={handleMenuOpen}
                                            color="inherit"
                                            sx={{
                                                minWidth: { xs: '44px', sm: 'auto' },
                                                minHeight: { xs: '44px', sm: 'auto' },
                                            }}
                                        >
                                            {profile?.avatar_url ? (
                                                <Avatar
                                                    src={profile.avatar_url}
                                                    alt={profile.username || profile.display_name || 'User'}
                                                    sx={{ width: 32, height: 32 }}
                                                />
                                            ) : (
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                                                    {((profile?.username || profile?.display_name || 'U').charAt(0)).toUpperCase()}
                                                </Avatar>
                                            )}
                                        </IconButton>
                                    </Badge>
                                    <UserMenu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleMenuClose}
                                        unreadCount={unreadCount}
                                        onInboxClick={() => navigate('/inbox')}
                                        onSettingsClick={() => setSettingsOpen(true)}
                                    />
                                </>
                            ) : (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)}
                                    sx={{
                                        textTransform: 'none',
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        color: 'inherit',
                                        '&:hover': { borderColor: 'inherit', backgroundColor: 'rgba(255,255,255,0.1)' },
                                    }}
                                >
                                    Sign In
                                </Button>
                            )}
                        </Box>
                    </Toolbar>
                    <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
                </AppBar>

                {/* Main Content */}
                <Container
                    component="main"
                    maxWidth="xl"
                    sx={{
                        flexGrow: 1,
                        py: { xs: 2, sm: 3 },
                        px: { xs: 1, sm: 2, md: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {children ?? <Outlet />}
                </Container>

                {/* Footer */}
                <ReusableFooter />
            </Box>

            {/* Welcome dialog for new users — shown on any authenticated page */}
            <WelcomeDialog
                open={welcomeOpen}
                onClose={handleWelcomeClose}
                username={profile?.username || profile?.display_name}
            />
        </Box>
    );
}
